import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from "@mui/material";
import { Button } from "@verily-src/react-design-system";
import { DeleteUserRequest, User } from "generated/studyauth/studyauth_pb";
import { useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { StudyAuthServiceApiClient } from "apiclient/StudyAuthServiceApiClient";
import { dialog_border_radius } from "styles/Dimensions";
import UpdateReasonForm, {
  UpdateReasonFormRef,
} from "components/UpdateReasonForm";
import A11yDialogTitle from "components/A11yDialogTitle";
import { telemetry, userNotAuthenticated } from "core/TelemetryController";

interface ConfirmDeleteSuperAdminDialogProps {
  /** Whether to open or close the dialog */
  open: boolean;
  user: User;
  onClose(): void;
  onSuccess(removeSelf: boolean): void;
}

// The confirmation dialog for deleting a super admin.
const ConfirmDeleteSuperAdminDialog: React.FC<
  ConfirmDeleteSuperAdminDialogProps
> = ({ open, user, onClose, onSuccess }) => {
  const userConfig = useAppSelector((state) => state.userConfig);
  const auth0Config = useAppSelector((state) => state.auth0Config);
  const testConfig = useAppSelector((state) => state.testConfig);

  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const updateReasonFormRef = useRef<UpdateReasonFormRef>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [disableConfirmButton, setDisableConfirmButton] = useState(false);

  const removeSelf = userConfig.userName === user.getName();

  const handleClickConfirmButton = () => {
    if (isAuthenticated && auth0Config) {
      (async () => {
        try {
          if (testConfig.showDeleteUserError) {
            throw new Error("failed to delete user");
          }

          if (
            !updateReasonFormRef.current!.validateInput(true /* focusOnError */)
          ) {
            return;
          }

          setDisableConfirmButton(true);

          const updateReason = updateReasonFormRef.current!.getUpdateReason();

          const token = await getAccessTokenSilently({
            audience: auth0Config.audience,
          });

          const client = new StudyAuthServiceApiClient(
            auth0Config.audience!,
            token
          );

          const request = new DeleteUserRequest()
            .setName(user.getName())
            .setUpdateReason(updateReason);
          await client.deleteUser(request);

          onSuccess(removeSelf);
        } catch (error) {
          telemetry.logError(error);
          setErrorMessage("Failed to remove the user. Please try again.");
        }

        setDisableConfirmButton(false);
      })();
    } else {
      telemetry.logError(new Error(userNotAuthenticated));
      setErrorMessage(
        "User is not authenticated. Please logout and login again."
      );
    }
  };

  // Set up initial state when the dialog is first opened.
  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setDisableConfirmButton(false);
    }
  }, [open]);

  const theme = useTheme();
  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        style: { borderRadius: dialog_border_radius },
        sx: { width: "500px !important" },
      }}
    >
      <A11yDialogTitle sx={{ paddingTop: "24px", paddingBottom: "4px" }}>
        <Typography variant="display6">
          {removeSelf ? "Remove yourself?" : "Remove user"}
        </Typography>
      </A11yDialogTitle>
      <DialogContent sx={{ paddingBottom: "16px" }}>
        {errorMessage.length > 0 && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            <AlertTitle>
              <Typography
                variant="body1em"
                color={theme.palette.error.textOnBackground}
              >
                Remove user failed
              </Typography>
            </AlertTitle>
            <Typography
              variant="body2"
              color={theme.palette.error.textOnBackground}
            >
              {errorMessage}
            </Typography>
          </Alert>
        )}
        <Typography
          variant="body1"
          sx={{ marginTop: "16px", marginBottom: "24px" }}
        >
          {removeSelf
            ? "You'll lose all access to Verily numetric watch portal. " +
              "To regain access, someone will have to re-invite you."
            : "This user won't be able to access Verily numetric watch portal. " +
              "Any pending invitations will be cancelled."}
        </Typography>
        <UpdateReasonForm
          ref={updateReasonFormRef}
          predefinedReasons={["Error correction", "Access no longer required"]}
          reasonSelectionLabel="Reason for removing user (required)"
          reasonRequiredMessage="Reason for removing user required"
          otherReasonHint="Please provide reason this user is being removed"
          otherReasonRequiredMessage="Other reason for removing user required"
        />
      </DialogContent>
      <DialogActions
        style={{ justifyContent: "right" }}
        sx={{ padding: "16px 24px 24px 24px" }}
      >
        <Button
          label="Cancel"
          onClick={onClose}
          variant="outlined"
          sx={{
            marginRight: "4px",
          }}
          data-testid="cancel-button"
        />
        <Button
          label="Remove"
          variant="filled"
          disabled={disableConfirmButton}
          onClick={handleClickConfirmButton}
          data-testid="continue-button"
        />
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteSuperAdminDialog;
